/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, switchMap } from 'rxjs';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-plan-dialog',
  templateUrl: './plan.dialog.html',
  standalone: false,
})
export class PlanDialog {
  constructor(
    private router: Router,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder,
    private propertySvc: PropertyService
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('plan') plan$ = new BehaviorSubject<Policy.Plan.Model | null>(null);
  @Input() plan: Policy.Plan.Model | null = null;

  @Output() closed = new EventEmitter<string>();

  page = 1;
  saving = false;
  addCondition = false;
  draft = Policy.Status.DRAFT;
  num = Property.PropertyType.NUMBER;

  form = this.formBuilder.group({
    title: ['', [Validators.required]],
    description: [''],
    order: [0, Validators.required],
    visible: [true],
    liabilityPerClaim: [false],
    planLiabilityLimit: ['', Validators.min(0)],
    planLiabilityLimitPropertyPath: [null as string | null],
    conditions: [{ comparison: Conditions.Comparison.AND, conditions: [] } as Conditions.ConditionGroup],
  });

  propertySet$ = this.policy$.pipe(
    switchMap(async policy => {
      if (!policy) return null;
      return await this.propertySvc.retrievePropertySet(policy.propertySetId);
    })
  );

  open() {
    this.page = 1;

    if (!this.plan) {
      this.form.reset({
        order: 0,
        visible: true,
        conditions: null,
        liabilityPerClaim: false,
        planLiabilityLimit: '',
        planLiabilityLimitPropertyPath: null,
      });
    } else {
      this.form.patchValue({
        order: this.plan.order,

        title: this.plan.title,
        description: this.plan.description,

        visible: this.plan.visible,
        conditions: this.plan.conditions,
        liabilityPerClaim: this.plan.liabilityPerClaim,
        planLiabilityLimit: this.plan.planLiabilityLimitPropertyPath
          ? ''
          : this.centsToDisplay(this.plan.planLiabilityLimit),
        planLiabilityLimitPropertyPath: this.plan.planLiabilityLimitPropertyPath ?? null,
      });

      if (this.form.value.conditions) this.addCondition = true;
    }

    this.dialog.open();
  }

  close() {
    this.dialog.close();
    this.page = 1;
    this.addCondition = false;
    this.form.reset({
      order: 0,
      visible: true,
      conditions: null,
      planLiabilityLimit: '',
      planLiabilityLimitPropertyPath: null,
    });
  }

  setPlanLiabilityLimitPropertyPath(
    property: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null
  ) {
    this.form.patchValue({
      planLiabilityLimitPropertyPath: property?.propertyPath ?? null,
      planLiabilityLimit: property ? '' : this.form.value.planLiabilityLimit,
    });
  }

  async create() {
    this.saving = true;

    const planDetails = this.form.value;

    if (this.policy) {
      const dto: Policy.Plan.CreatePolicyPlanRequest = {
        canCreateNewVersion: true,
        order: Number(planDetails.order ?? 0),

        title: planDetails.title || '',
        description: planDetails.description || '',

        visible: !!planDetails.visible,
        liabilityPerClaim: !!planDetails.liabilityPerClaim,
        planLiabilityLimit: planDetails.planLiabilityLimitPropertyPath
          ? 0
          : this.dollarsToCents(planDetails.planLiabilityLimit),
        planLiabilityLimitPropertyPath: planDetails.planLiabilityLimitPropertyPath ?? null,
        conditions: planDetails.conditions?.conditions?.length ? planDetails.conditions : null,
      };

      const policyId = this.policy.id;

      const plan = await this.policySvc.createPlan(policyId, dto).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.close();
        this.saving = false;
      });

      if (plan) {
        if (this.policy.id === plan.policyId) {
          this.toastSvc.show({
            type: 'success',
            title: 'Created plan',
            text: '<strong>' + planDetails.title + '</strong> has been successfully created.',
          });
          await this.refreshPlanAndPolicy(plan.id, plan.policyId);
        } else {
          this.toastSvc.show({
            type: 'success',
            title: 'New policy version created',
            text:
              '<strong>' +
              planDetails.title +
              '</strong> has been successfully created and a new version of <strong>' +
              this.policy.title +
              '</strong> has been created.',
          });

          await this.refreshPlanAndPolicy(plan.id, plan.policyId);
          await this.navigateTo('/policies/' + plan.policyId + '/plans/' + plan.id);
        }

        this.close();
        this.saving = false;
      }
    }

    this.saving = false;
  }

  async update() {
    this.saving = true;

    const planDetails = this.form.value;

    if (this.policy && this.plan) {
      const policyId = this.policy.id;
      const planId = this.plan.id;

      const dto: Policy.Plan.UpdatePolicyPlanRequest = {
        canCreateNewVersion: true,
        order: Number(planDetails.order ?? 0),

        title: planDetails.title || '',
        description: planDetails.description || '',

        liabilityPerClaim: !!planDetails.liabilityPerClaim,

        planLiabilityLimit: planDetails.planLiabilityLimitPropertyPath
          ? 0
          : this.dollarsToCents(planDetails.planLiabilityLimit),
        planLiabilityLimitPropertyPath: planDetails.planLiabilityLimitPropertyPath ?? null,

        conditions: planDetails.conditions?.conditions?.length ? planDetails.conditions : null,
      };

      let plan: Policy.Plan.UpdatePolicyPlanResponse | null = null;

      try {
        plan = await this.policySvc.updatePlan(planId, policyId, dto);
      } catch (error) {
        const nextTarget = await this.resolvePlanVersionFromError(error);
        if (nextTarget) {
          this.toastSvc.show({
            type: 'info',
            title: 'New policy version created',
            text: 'Policy has been updated to a new version as draft.',
          });
          try {
            plan = await this.policySvc.updatePlan(nextTarget.plan.id, nextTarget.policy.id, dto);
          } catch (retryError) {
            this.handlePlanRequestError(retryError);
            return;
          }
        } else {
          this.handlePlanRequestError(error);
          return;
        }
      }

      if (plan) {
        if (this.policy.id === plan.policyId) {
          this.toastSvc.show({
            type: 'success',
            title: 'Updated plan',
            text: '<strong>' + planDetails.title + '</strong> has been successfully updated.',
          });

          await this.refreshPlanAndPolicy(plan.id, plan.policyId);
        } else {
          this.toastSvc.show({
            type: 'success',
            title: 'New policy version created',
            text:
              '<strong>' +
              planDetails.title +
              '</strong> has been successfully updated and a new version of <strong>' +
              this.policy.title +
              '</strong> has been created.',
          });

          await this.refreshPlanAndPolicy(plan.id, plan.policyId);
          await this.navigateTo('/policies/' + plan.policyId + '/plans/' + plan.id);
        }

        this.close();
        this.saving = false;
      }
    }

    this.saving = false;
  }

  async navigateTo(path?: string) {
    if (path) await this.router.navigate([`${path}`]);
  }

  private centsToDisplay(amount?: number | null): string {
    if (amount === null || amount === undefined) return '';
    return (amount / 100).toString();
  }

  private dollarsToCents(amount: number | string | null | undefined): number {
    const normalizedAmount =
      typeof amount === 'number' ? amount : typeof amount === 'string' ? this.parseLocalizedNumber(amount) : 0;

    if (Number.isNaN(normalizedAmount) || normalizedAmount <= 0) return 0;
    return Math.round(normalizedAmount * 100);
  }

  private parseLocalizedNumber(value: string): number {
    const trimmed = value?.trim();
    if (!trimmed) return 0;

    const normalized = trimmed.replace(/\s/g, '');
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    if (hasComma && hasDot) {
      const decimalSeparator = normalized.lastIndexOf(',') > normalized.lastIndexOf('.') ? ',' : '.';
      const thousandSeparator = decimalSeparator === ',' ? '.' : ',';

      const withoutThousands = normalized.replace(new RegExp('\\' + thousandSeparator, 'g'), '');
      const standardized = decimalSeparator === ',' ? withoutThousands.replace(/,/g, '.') : withoutThousands;

      return parseFloat(standardized);
    }

    if (hasComma) {
      const parts = normalized.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        return parseFloat(normalized.replace(/\./g, '').replace(',', '.'));
      }

      return parseFloat(normalized.replace(/,/g, ''));
    }

    if (hasDot) {
      const parts = normalized.split('.');
      if (parts.length === 2 && parts[1].length <= 2) {
        return parseFloat(normalized);
      }

      return parseFloat(normalized.replace(/\./g, ''));
    }

    return parseFloat(normalized);
  }

  private async refreshPlanAndPolicy(planId: string, policyId: string) {
    await Promise.all([
      this.policySvc.refreshPolicy(policyId),
      this.policySvc.refreshPlan({ id: planId, policyId } as Policy.Plan.Model, {
        includeCoverageConfigs: false,
        includeFees: false,
        includeCoverageConfigBlocks: false,
      }),
    ]);
  }

  private handlePlanRequestError(error: any) {
    const message = error?.error?.message || 'An unexpected error occurred.';
    const status = error?.status;

    const title = 'Unable to save plan';
    let text = message || 'An unexpected error occurred. Please try again.';

    switch (status) {
      case 400:
      case 422:
        text = message || 'Please review the form details and try again.';
        break;
      case 403:
        text = 'You do not have permission to edit this plan.';
        break;
      case 404:
        text = message || 'We could not find this plan. Refresh the policy and try again.';
        break;
      case 500:
      default:
        text = message || 'Something went wrong on our end. Please try again in a moment.';
        break;
    }

    this.toastSvc.show({
      type: 'error',
      title,
      text,
    });

    this.close();
    this.saving = false;
  }

  private async resolvePlanVersionFromError(error: any) {
    if (!this.policy || !this.plan) return null;
    if (error?.status !== 404) return null;

    const message = (error?.error?.message || '').toLowerCase();
    if (!message.includes('plan')) return null;

    const currentPolicy = await this.policySvc.getOne(this.policy.id);
    const succeededById = currentPolicy?.succeededById;
    if (!succeededById) return null;

    const newPolicy = await this.policySvc.getOne(succeededById);
    if (!newPolicy?.plans?.length) return null;

    // Consolidate fallback chain into a single pass
    const criteria = [
      (newPlan: any) => newPlan.id === this.plan?.id,
      (newPlan: any) => newPlan.title === this.plan?.title && newPlan.order === this.plan?.order,
      (newPlan: any) => newPlan.order === this.plan?.order,
      (newPlan: any) => newPlan.title === this.plan?.title,
      (newPlan: any) => newPlan.planLiabilityLimit === this.plan?.planLiabilityLimit,
    ];
    const scoredPlans = newPolicy.plans
      .map(newPlan => {
        // Find the highest-priority criterion this plan matches
        let score = -1;
        for (let i = 0; i < criteria.length; i++) {
          if (criteria[i](newPlan)) {
            score = i;
            break;
          }
        }
        return { plan: newPlan, score };
      })
      .filter(p => p.score !== -1);
    // Pick the plan with the lowest score (highest priority)
    const replacementPlan = scoredPlans.length
      ? scoredPlans.reduce((best, current) => (current.score < best.score ? current : best)).plan
      : null;

    if (!replacementPlan) return null;

    return { policy: newPolicy, plan: replacementPlan };
  }
}
